import {
  PaywallApi,
  PaywallProps,
  PaywallSession,
  PluginType,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { purchaseService } from '#/client';
import { useGlobalContext } from '#/components/useGlobalContext';
import { useConfig } from '#/hooks';
import { usePaywallConfig } from '#/hooks/usePaywallConfig';
import { getPaywallSession } from '#/plugins/paywall/utils';
import { ignoreError } from '#/service/utils';
import { authActions, useAuthentication } from '#/store/auth';
import { paywallActions, usePaywall } from '#/store/paywall';
import { useTranslations } from '#/store/translation/translation.store';

import pluginManager from '../pluginManager';

const onError = (error: Error, stack: string) => {
  console.error('Paywall plugin error', error, stack);
};

const PaywallManager = () => {
  const {
    boot: { isPluginManagerReady },
  } = useGlobalContext();

  const apiRef = useRef<PaywallApi>(null);
  const { productIds, isPluginReady } = usePaywall();
  const paywallConfig = usePaywallConfig();
  const auth = useAuthentication();
  const { locale } = useTranslations();
  const config = useConfig();
  const isInAppEnabled = config.features.purchase?.inApp?.enabled || false;
  const [localProductIds, setLocalProductIds] = useState<string[]>([]);

  const { sessionEndpoint } = paywallConfig?.properties ?? {};

  useEffect(() => {
    // apiRef.current will change value on login/logout, so we need to re-init the paywallApi (ie: ignoring isPluginReady)
    if (apiRef.current) {
      paywallActions.initPlugin(apiRef);
    }

    if (!(auth.isInitialised && auth.isLoggedIn && isInAppEnabled)) {
      return;
    }

    purchaseService
      .getProducts(auth.purchaseEntitlements)
      .then((res) => {
        paywallActions.setProductIds(res.data.productIds);
      })
      .catch(ignoreError);
  }, [
    auth.isInitialised,
    auth.isLoggedIn,
    auth.purchaseEntitlements,
    isPluginReady,
    isInAppEnabled,
  ]);

  useEffect(() => {
    // We only want to fetch plans when product Ids have come in and the api has been re-initialized.
    const isProductIdsEqual =
      localProductIds.toString() === productIds.toString();

    // Product IDs match, no need to fetch more.
    if (isProductIdsEqual || !productIds.length) {
      return;
    }
    setLocalProductIds(productIds);

    paywallActions.initPlugin(apiRef).then(() => {
      if (!isProductIdsEqual) {
        paywallActions.refreshPlans();
      }
    });
  }, [localProductIds, productIds]);

  /**
   * This function is no longer in use, and only maintained for backwards compatibility
   * SEE https://accedobroadband.jira.com/browse/ACCEDOONECA-16982
   *
   *
   * Gets session token, but to make sure we are up to date we refresh the profile first.
   * And we analyze profile response to see if there is an entitlement error. If so,
   * we should prevent showing the paywall.
   */
  const getSession = useCallback<
    () => Promise<PaywallSession | null>
  >(async () => {
    const profile = await authActions.refreshProfile();

    if (profile?.errors?.['entitlements']) {
      return Promise.reject('Entitlements error');
    }

    return sessionEndpoint
      ? getPaywallSession(sessionEndpoint).then(({ data }) => data)
      : Promise.resolve(null);
  }, [sessionEndpoint]);

  const PaywallComponent = useMemo(() => {
    if (!paywallConfig?.plugin?.id || !isPluginManagerReady) {
      return null;
    }

    return pluginManager.getPluginComponent<PaywallProps>({
      type: PluginType.Paywall,
      id: paywallConfig.plugin.id,
      optional: true,
    });
  }, [paywallConfig, isPluginManagerReady]);

  if (!PaywallComponent) {
    return null;
  }

  return (
    <ErrorBoundary onError={onError}>
      <Suspense fallback={null}>
        <PaywallComponent
          apiRef={apiRef}
          authState={auth}
          getSession={getSession}
          key={`${auth.isLoggedIn}`}
          locale={locale}
          purchasablePlanIds={productIds}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

PaywallManager.displayName = 'PaywallManager';

export default PaywallManager;
